import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { SuccessAlertContext } from "App";
import Notify from "config/notify";
import { CSVCountCheckCsvStock, CSVThresholdCheckStock } from "../types";
import TableCellSmall, {
  MultiTypography,
  TableCellWide,
  TableHeaderCellSmall,
} from "./TableCellSmall";

const limit = 10;

type Props = {
  open: boolean;
  title: string;
  content: string;
  ActionButtons: JSX.Element;
  onClose: Function;
  stockList: (CSVCountCheckCsvStock | CSVThresholdCheckStock)[];
  isFastLoading?: MutableRefObject<boolean>;
  getMoreStockList?: Function;
  setInit?: Dispatch<SetStateAction<boolean>>;
  isLoading?: boolean;
  error?: string;
  errorTitle?: string;
  // 画面からupdateする際に仕入先を見る時使用
  isSupplier?: boolean; // CSVではないupdateの時のみ仕入先ユーザーであればisSupplierに入力
};
/**
 * 在庫数量更新確認モーダルのテーブル
 * @param root0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function SupplierConfirmCsvTable({
  open,
  title,
  content,
  ActionButtons,
  onClose,
  stockList,
  isFastLoading,
  getMoreStockList,
  setInit,
  isLoading,
  error,
  errorTitle,
  // 画面からupdateする際に仕入先を見る時使用
  isSupplier,
}: Props) {
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false);
  const [adjustment, setAdjustment] = useState<number | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement | undefined>(undefined);
  const innerRef = useRef<HTMLDivElement | undefined>(undefined);
    // CSVの仕入先の場合に使用
  const [selectedRegionWarehouseIndex, setSelectedRegionWarehouseIndex] = useState<number>(0); // 地域倉庫のindex(supplierWarehouseのindexではなく!isCenterでfilterされたindex)
  const [selectedCenterWarehouseIndex, setSelectedCenterWarehouseIndex] = useState<number>(0); // センターのindex(supplierWarehouseのindexではなくisCenterでfilterされたindex)
  const { showAlert } = useContext(SuccessAlertContext);

  // スクロール処理
  const handleScroll = async () => {
    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading?.current || !contentRef.current) {
      return;
    }
    const scrollTop = contentRef.current?.scrollTop;
    const contentHeight = contentRef.current?.clientHeight;
    const scrollHeight = contentRef.current?.scrollHeight;
    if (scrollTop + contentHeight + 10 >= scrollHeight) {
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      if (getMoreStockList) await getMoreStockList();
    })();
  }, [fireMoreLoadEvent]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (setInit) {
      setInit(true);
      // if (error) showAlert(error, "error");
      if (error) {
        Notify?.setNotification?.({
          show: true,
          title: "CSVアップロードでエラーがあります。",
          message: error,
          messageWhiteSpace: "pre-line",
        });
      }
    }
  }, [open]);

  // 表示数がlimitの時、中のコンテンツが小さいと無限ローディングさせることができないため、少しスクロールできるようにする。
  useEffect(() => {
    if (stockList.length === limit && !isLoading) {
      // console.log('adjustment', adjustment, stockList.length, contentRef.current?.clientHeight);
      setTimeout(() => {
        if (
          contentRef.current &&
          innerRef.current &&
          contentRef.current.clientHeight - innerRef.current.clientHeight >= 0
        ) {
          // 中のコンテンツが小さい場合、調整オブジェクトを挿入
          setAdjustment(contentRef.current.clientHeight - innerRef.current.clientHeight + 1);
        } else {
          setAdjustment(undefined);
        }
      }, 0);
    } else {
      setAdjustment(undefined);
    }
  }, [stockList, isLoading]);
  return (
    <Dialog open={open} onClose={() => onClose()} fullScreen>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent ref={contentRef} onScroll={handleScroll}>
        <Box ref={innerRef}>
          <DialogContentText>{error ? errorTitle : content}</DialogContentText>
          <TableContainer
            component={Paper}
            sx={{ overflowX: "initial", width: "fit-content", minWidth: "100%" }}
          >
            <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="order table">
              {stockList.length !== 0 &&
                <TableHead>
                  <TableRow>
                    <TableHeaderCellSmall>商品コード</TableHeaderCellSmall>
                    <TableHeaderCellSmall>JANコード</TableHeaderCellSmall>
                    <TableHeaderCellSmall>在庫数量</TableHeaderCellSmall>
                    <TableHeaderCellSmall>入荷予定日</TableHeaderCellSmall>
                    <TableHeaderCellSmall>倉庫コード</TableHeaderCellSmall>
                    {error && <TableHeaderCellSmall>エラー内容</TableHeaderCellSmall>}
                  </TableRow>
                </TableHead>
              }

              <TableBody>
                {
                  stockList.map((s) => {
                  // eslint-disable-next-line no-param-reassign
                  s = s as CSVCountCheckCsvStock;
                  return (
                    <TableRow
                      key={s.productCd}
                      sx={error && s.error ? { backgroundColor: "var(--red-400)" } : {}}
                    >
                      <TableCellSmall>{s.productCd}</TableCellSmall>
                      <TableCellSmall>{s.JANCd}</TableCellSmall>
                      <TableCellSmall>
                        {typeof s.amount === "number" ? s.amount.toLocaleString() : s.amount}
                      </TableCellSmall>
                      <TableCellSmall>{s.arrivalDate}</TableCellSmall>
                      {"warehouseCd" in s ? (
                        <TableCellSmall>{s.warehouseCd}</TableCellSmall>
                      ) : (
                        <TableCellSmall />
                      )}
                      {error &&
                        (s.error ? (
                          <TableCellWide>
                            <MultiTypography>{s.error}</MultiTypography>
                          </TableCellWide>
                        ) : (
                          <TableCellWide />
                        ))
                      }
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {adjustment && ( // 画面が大きいと無限スクロールが発火しないことがある
            <Box height={adjustment} />
          )}
          {isLoading && (
            <Box py={3} textAlign="center">
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>{ActionButtons}</DialogActions>
    </Dialog>
  );
}
